/* eslint-disable no-console */
/* eslint-disable newline-before-return */
import {
  IExistingUpload,
  isExistingUpload,
  isUploadWithFile,
  IUpload,
  IUploadWithFile,
} from '@stormwater-software/lib-form';
import instance, { xhrService } from '../xhrService';
import db, { offlineImageCache } from '../../db/db';
import uploadApi, { fileToBase64 } from './upload';
import { v4 as uuidv4 } from 'uuid';
import { getDateOnly } from '../../utils/date';
import { withOfflineFallback, withOfflineWrite } from '../offlineFallback';

export type Finding = {
  id: number;
  type: string;
  date_completed: Date | null;
  date_initiated: Date | null;
};

/**
 * Findings by inspection
 */
const url = (a: number) => `/api/inspections/${a}/findings`;

function generateId(id: number, findings: any) {
  if (id !== undefined && id !== null && id !== 0) return id;
  if (findings && findings.length > 0) {
    return findings[findings.length - 1].id + Math.floor(Math.random() * 100);
  }
  return Math.floor(Math.random() * 1000);
}

async function uploadImagesOffline<T extends { id: number }>(
  finding: T,
  newUploads: IUploadWithFile[] = [],
  online: boolean = false,
  // SW2-2136: stored with the queued photo so the sync uploads it as what it is. Photos taken
  // offline used to carry no type and synced as 'initial', so closure photos printed as Initial
  // and could never meet a closure-photo requirement.
  photoType: 'initial' | 'progress' | 'closure' = 'initial',
): Promise<T & { finding_uploads: any[] }> {
  const findingObj = await db.findings.get(finding.id).catch((error: any) => {
    console.log('Error getting offline finding', error);
  });

  if (!findingObj) {
    // Expected when the inspection was never synced for offline use — the
    // offline mirror is skipped and the online flow is unaffected.
    console.log(
      'Offline mirror: finding not in local db, skipping offline upload:',
      finding.id,
    );
    return { ...finding, finding_uploads: [] };
  }

  const newImgArr: any = [];

  for (const upload of newUploads) {
    const {
      file,
      name,
      annotations = [],
      rotation = 0,
      type,
      timestamp,
      gps_latitude,
      gps_longitude,
    } = upload;
    const guid = upload.guid ? upload.guid : uuidv4();

    const base64File = await fileToBase64(file);

    // SW2-2449: the photo's own date (EXIF) or none. This used to fall back to the time the
    // photo was queued, which saved as if it were when the photo was taken, and an API without
    // the SW2-2449 fix rejected that ISO string with a 500 'Invalid date'.
    const timeStamp = timestamp || null;
    const gpsData =
      gps_latitude && gps_longitude
        ? { lat: gps_latitude, lon: gps_longitude }
        : null;

    newImgArr.push({
      annotations: annotations,
      createdAt: new Date().toISOString(),
      deletedAt: null,
      finding_id: finding.id,
      is_completed: photoType === 'closure',
      photo_type: photoType,
      timestamp: timeStamp,
      gpsData: gpsData,
      id: guid,
      upload: {
        GUID: guid,
        mime_type: type,
        name: name,
        timestamp: timeStamp,
        rotation,
        gps_latitude: gps_latitude,
        gps_longitude: gps_longitude,
        archived_at: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        deletedAt: null,
      },
    });

    // `payload.file` carries the upload metadata only. The raw File is not
    // persisted: iOS stores it as a reference into Safari's temp area, and once
    // iOS reclaims that file the row still reads back as a File with no bytes
    // behind it, so the sync sent an empty multipart body and the API answered
    // 400 "No file attached". The Base64 copy below is the durable one and sync
    // rebuilds the File from it.
    const syncObj = {
      synced: false,
      payload: {
        file: {
          annotations,
          rotation,
          type,
          name,
          gpsData,
          timestamp: timeStamp,
        },
        payload_id: finding.id,
        photo_type: photoType,
      },
    };

    if (online) {
      syncObj.synced = true;
    }

    await db.images.add({
      guid: guid,
      file: base64File,
      type: type,
      name: name,
      payload_id: finding.id,
      photo_type: photoType,
      sync: {
        ...syncObj,
      },
    });

    offlineImageCache[guid] = base64File;
  }

  await db.findings.update(finding.id, {
    finding_uploads: [
      ...(findingObj.finding_uploads ? findingObj.finding_uploads : []),
      ...newImgArr.map((upload: any) => ({
        ...upload,
        id: upload.id,
        finding_id: finding.id,
      })),
    ],
  });

  return {
    ...finding,
    finding_uploads: [
      ...(findingObj.finding_uploads ? findingObj.finding_uploads : []),
      ...newImgArr.map((upload: any) => ({
        ...upload,
        id: upload.id,
        finding_id: finding.id,
      })),
    ],
  };
}

/**
 * SW2-2136 — photos that already reached the server, by guid → their finding_upload id, so a
 * retry after a refused save (a signed inspection, an invalid type, a closure-photo refusal)
 * sends them as existing instead of uploading them again.
 *
 * Recorded here, NOT written onto the photo: these are the form's own objects, and lib-form's
 * image field accepts a photo with a file and no id (new) or an id and no file (existing), and
 * throws on anything else. Setting `id` on a photo that still held its file crashed Finding
 * Detail with "`value` must implement IUpload[]".
 */
const uploadedPhotoIds = new Map<string, number>();

function markUploaded(images: any[] | undefined, rows: any[] | undefined) {
  (images || []).forEach((image: any) => {
    if (image.id || !image.guid) return;

    const row = (rows || []).find(
      (upload: any) => upload.upload && upload.upload.GUID === image.guid,
    );

    if (row) uploadedPhotoIds.set(image.guid, row.id);
  });
}

/**
 * Rejects with a user-readable error when a selected photo can no longer be
 * read from disk (0-byte or unreadable File handles produce empty uploads).
 */
async function assertFilesReadable(newUploads: IUploadWithFile[]) {
  for (const upload of newUploads) {
    const file: any = upload.file;

    // Offline-sync flows can carry base64 strings instead of File objects
    if (typeof file === 'string') continue;

    let unreadable = !file || file.size === 0;

    if (!unreadable) {
      try {
        await file.slice(0, 1).arrayBuffer();
      } catch {
        unreadable = true;
      }
    }

    if (unreadable) {
      throw new Error(
        `The photo "${
          upload.name || (file && file.name) || 'selected'
        }" could not be read. Please re-select it and try again.`,
      );
    }
  }
}

async function uploadImages<T extends { id: number }>(
  finding: T,
  images: IUpload[] = [],
  online: boolean = false,
  photoType: 'initial' | 'progress' | 'closure' = 'initial',
): Promise<T & { finding_uploads: any[] }> {
  if (images.length === 0) {
    return { ...finding, finding_uploads: [] };
  }

  const [newUploads, existingUploads] = extractUploads(images);

  await assertFilesReadable(newUploads);

  // SW2-2321: was a bare `navigator.onLine` pre-check. It reports true after an
  // offline reload, so the upload went to the network, failed, and never reached
  // uploadImagesOffline — the inspector's photos were dropped with no error.
  //
  // The post-success work below (validating the returned rows and assigning
  // GUIDs) stays inside the request: a throw from there is not network-shaped, so
  // withOfflineWrite rethrows rather than queueing uploads the server already has.
  return withOfflineWrite<T & { finding_uploads: any[] }>(
    async () => {
      const uploads = await uploadApi.finding
        .update(finding.id, newUploads, existingUploads, photoType)
        .then((fUploads) => {
          return {
            ...finding,
            finding_uploads: fUploads,
          };
        });

      // The server returns ALL of the finding's uploads ordered created_at ASC,
      // so the rows for the files we just sent are the tail of the list. Validate
      // instead of blindly indexing from 0 so a dropped upload surfaces as an
      // error rather than silently mis-assigning GUIDs.
      if (newUploads.length > 0) {
        const allUploads = uploads.finding_uploads || [];
        const newRows = allUploads.slice(
          Math.max(0, allUploads.length - newUploads.length),
        );

        if (
          newRows.length < newUploads.length ||
          newRows.some((row: any) => !row || !row.upload || !row.upload.GUID)
        ) {
          throw new Error(
            'The server did not confirm all uploaded photos. Please re-open the finding and re-upload any missing photos.',
          );
        }

        for (let i = 0; i < newUploads.length; i++) {
          newUploads[i].guid = newRows[i].upload.GUID;
        }
      }

      if (online) {
        await uploadImagesOffline(finding, newUploads, true, photoType);
      }

      return uploads;
    },
    () => uploadImagesOffline(finding, newUploads, false, photoType),
  );
}

/**
 * Creates a finding record offline for an inspection
 * @param inspectionId - The ID of the inspection
 * @param formData - Form data containing finding details and images
 * @returns The newly created finding object
 */
async function createFindingOffline(
  inspectionId: number,
  formData: { [k: string]: any; images: IUpload[] },
  online: boolean = false,
  id: number = 0,
): Promise<any> {
  const { images, ...data } = formData;
  const user = await db.user.get(1);

  if (!user) {
    return;
  }

  // Create commentable objects if comments exist
  const commentableObj = data.comments
    ? [
        {
          author: {
            name: user!.name,
            id: user!.user_id,
            first_name: user!.first_name,
            last_name: user!.last_name,
          },
          content: data.comments,
          observation_type: 'initial',
          created_at: new Date().toISOString(),
        },
      ]
    : [];

  // Get inspection details
  const inspection = await db.inspections.get(inspectionId);

  if (!inspection) {
    return;
  }

  const templateId = inspection!.template_id;
  const inspectionFindings = inspection!.findings;

  // Get existing findings and certification
  const findings = await db.findings
    .where('id')
    .anyOf(inspectionFindings.map((f: any) => f.id))
    .toArray();
  const existingCertification = await db.certifications.get(inspectionId);

  if (!existingCertification || !findings) {
    console.log(
      'No existing certification found for inspection:',
      inspectionId,
    );
    return;
  }

  // Get finding type details
  const types = await db.findingTypeInspectionTemplate.get(Number(templateId));
  const findingType = types.findingTypes.find(
    (ft: any) => ft.name === data.type,
  );
  const findingTypeObj = {
    is_static_type: findingType.is_static_type,
    is_required_for_cert: findingType.details.is_required_for_cert,
    finding_type_id: findingType.id,
    color: findingType.details.color,
    is_enabled: findingType.details.is_enabled,
  };

  // Format dates
  const date_initiated = getDateOnly(data.date_initiated) || null;
  const date_completed = data.date_completed
    ? getDateOnly(data.date_completed)
    : null;
  const { comments, ...rest } = data;

  // Create finding object
  const findingObj = {
    id: generateId(id, findings),
    InspectionFindingModel: {
      carried_over: false,
      inspection_id: inspectionId,
    },
    ...rest,
    ...findingTypeObj,
    inspection_id: inspectionId,
    date_initiated,
    number:
      findings && findings.length
        ? findings[findings.length - 1].number + 1
        : 1,
    commentables: commentableObj,
    date_completed,
    sync: {
      synced: false,
      payload: [
        {
          method: 'create',
          ...data,
        },
      ],
    },
  };

  if (online) {
    findingObj.sync.synced = true;
    findingObj.sync.payload = [];
  }
  // Add finding to database
  const findingId = await db.findings.add(findingObj).catch((error: any) => {
    console.log('Error creating offline finding', error);
    return Promise.reject(error);
  });

  // Update inspection with new finding
  await db.inspections
    .update(inspectionId, {
      findings: [...inspectionFindings, findingObj],
    })
    .catch((error: any) => {
      console.log('Error creating offline finding', error);
      return Promise.reject(error);
    });

  // Return the newly created finding
  const finding = await db.findings.get(findingId).catch((error: any) => {
    console.log('Error creating offline finding', error);
    return Promise.reject(error);
  });

  // // Update certification if all required findings are completed
  const requiredFindings = [...findings, finding].filter(
    (f: any) => f.is_required_for_cert,
  );
  const completedRequiredFinings = requiredFindings.filter(
    (f: any) => f.date_completed,
  );

  if (
    requiredFindings.length === completedRequiredFinings.length &&
    existingCertification.groupOptions['compliance'] &&
    existingCertification.groupOptions['compliance'].errors.length === 0
  ) {
    existingCertification.groupOptions['compliance'].enabled = true;
    existingCertification.groupOptions['compliance'].errors = [];

    await db.certifications
      .update(inspectionId, existingCertification)
      .catch((error: any) => {
        console.log('Error updating offline certification', error);
        return Promise.reject(error);
      });
  } else if (existingCertification.groupOptions['compliance']) {
    existingCertification.groupOptions['compliance'].enabled = false;
    if (
      existingCertification.groupOptions['compliance'].errors.indexOf(
        'Findings need to be completed prior to signing',
      ) === -1
    ) {
      existingCertification.groupOptions['compliance'].errors.push(
        'Findings need to be completed prior to signing',
      );
    }

    await db.certifications
      .update(inspectionId, existingCertification)
      .catch((error: any) => {
        console.log('Error updating offline certification', error);
        return Promise.reject(error);
      });
  }

  return { ...finding, dissolveFindingImages: false };
}

/**
 * Updates a finding record in an offline database and handles related records
 * @param id The ID of the finding to update
 * @param formData Form data containing finding details and images
 * @returns The updated finding object
 */
async function updateFindingOffline(
  id: number,
  formData: { [k: string]: any; images: IUpload[] },
  online: boolean = false,
): Promise<any> {
  const { images, ...data } = formData;

  try {
    // Get current user
    const user = await db.user.get(1);

    // Get existing finding
    const oldFinding = await db.findings.get(id);

    if (!oldFinding || !user) {
      // Expected when the inspection was never synced for offline use — the
      // offline mirror is skipped and the online flow is unaffected.
      console.log(
        'Offline mirror: finding not in local db, skipping offline update:',
        id,
      );
      return;
    }

    // Process comments if provided
    const commentableObj: any[] = data.comments
      ? [
          {
            author: {
              name: user!.name,
              id: user!.user_id,
              first_name: user!.first_name,
              last_name: user!.last_name,
            },
            content: data.comments,
            observation_type: data.date_completed ? 'final' : 'progress',
            created_at: new Date().toISOString(),
          },
        ]
      : [];

    // Combine existing and new comments
    const updatedCommentables = [...oldFinding.commentables, ...commentableObj];

    // Format dates
    const date_initiated = data.date_initiated
      ? getDateOnly(data.date_initiated)
      : null;
    const date_completed = data.date_completed
      ? getDateOnly(data.date_completed)
      : null;

    const existingFinding = await db.findings.get(id);

    const { comments, ...rest } = data;

    const syncObj = {
      // SW2-2443: keep what the offline sync records on the row (`serverId`);
      // replacing the object dropped it, so a queued edit lost its server id.
      ...existingFinding.sync,
      synced: false,
      payload: [
        ...existingFinding.sync?.payload,
        {
          method: 'update',
          ...data,
          type: data.bulk ? existingFinding.type : data.type,
          date_initiated: data.bulk
            ? existingFinding.date_initiated
            : date_initiated,
          location: data.bulk ? existingFinding.location : data.location,
        },
      ],
    };

    if (online) {
      syncObj.synced = true;
      syncObj.payload = [];
    }

    // Update finding record
    await db.findings.update(id, {
      ...rest,
      date_initiated: data.bulk
        ? existingFinding.date_initiated
        : date_initiated,
      date_completed,
      commentables: updatedCommentables,
      sync: {
        ...syncObj,
      },
    });

    // Get inspection and related findings
    const inspection = await db.inspections.get(Number(data.inspectionId));

    if (!inspection) {
      console.log('Inspection not found for update:', data.inspectionId);
      return;
    }
    const inspectionFindings = inspection!.findings;
    const findings = await db.findings
      .where('id')
      .anyOf(inspectionFindings.map((f: any) => f.id))
      .toArray();

    // Check if all required findings are completed
    const requiredFindings = findings.filter(
      (f: any) => f.is_required_for_cert && f.id !== id,
    );
    const completedRequiredFindings = requiredFindings.filter(
      (f: any) => f.date_completed,
    );

    // Update certification if all required findings are completed
    if (requiredFindings.length === completedRequiredFindings.length) {
      const existingCertification = await db.certifications.get(
        Number(data.inspectionId),
      );

      if (!existingCertification) {
        console.log(
          'Certification not found for inspection:',
          data.inspectionId,
        );
        return;
      }

      existingCertification.groupOptions['compliance'].enabled = true;
      existingCertification.groupOptions['compliance'].errors = [];
      existingCertification.minCertificationDate =
        date_completed &&
        date_completed > existingCertification.minCertificationDate
          ? date_completed
          : existingCertification.minCertificationDate;

      await db.certifications.update(
        Number(data.inspectionId),
        existingCertification,
      );
    } else {
      const existingCertification = await db.certifications.get(
        Number(data.inspectionId),
      );

      existingCertification.groupOptions['compliance'].enabled = false;
      if (
        existingCertification.groupOptions['compliance'].errors.indexOf(
          'Findings need to be completed prior to signing',
        ) === -1
      ) {
        existingCertification.groupOptions['compliance'].errors.push(
          'Findings need to be completed prior to signing',
        );
      }

      await db.certifications.update(
        Number(data.inspectionId),
        existingCertification,
      );
    }

    // Get updated finding
    const finding = await db.findings.get(id);

    // Update inspection with the updated finding
    await db.inspections.update(Number(data.inspectionId), {
      findings: [
        ...inspection!.findings.filter((f: any) => f.id !== id),
        finding,
      ],
    });

    return { ...finding, dissolveFindingImages: false };
  } catch (error) {
    console.log('Error updating offline finding', error);
    return Promise.reject(error);
  }
}

function extractUploads(
  images: IUpload[],
): [IUploadWithFile[], IExistingUpload[]] {
  const newUploads: IUploadWithFile[] = [];
  const existingUploads: IExistingUpload[] = [];

  images.forEach((image) => {
    const uploadedId =
      isUploadWithFile(image) && image.guid ? uploadedPhotoIds.get(image.guid) : undefined;

    if (uploadedId !== undefined) {
      // Already on the server (see uploadedPhotoIds): sent as the existing upload it now is.
      const { file, ...rest } = image as any;

      existingUploads.push({ ...rest, id: uploadedId } as IExistingUpload);
    } else if (isUploadWithFile(image)) {
      newUploads.push(image);
    } else if (isExistingUpload(image)) {
      existingUploads.push(image);
    }
  });

  return [newUploads, existingUploads];
}

export const findingApi = {
  index: async (id: number, excludeCompleted: boolean = false) =>
    // SW2-2321: converted from a `navigator.onLine` pre-check. After an offline
    // reload the findings list was empty even though the records were cached.
    withOfflineFallback<any>(
      () =>
        instance
          .get(url(id), { params: { excludeCompleted } })
          .then((response) => response.data),
      async () => {
        const inspection = await db.inspections.get(Number(id));

        // No cached inspection means no cached answer. Falling through to
        // `anyOf([])` returned `[]`, which withOfflineFallback treats as a hit
        // and reports as "this inspection has no findings" — hiding the request
        // failure. An inspection that *is* cached and genuinely has no findings
        // must still return `[]`, because that is a real answer.
        // (Raised by Copilot on PR #53.)
        if (!inspection) {
          return null;
        }

        const findingIds = inspection.findings.map((f: any) => f.id);

        return db.findings.where('id').anyOf(findingIds).toArray();
      },
    ),

  show: async (id: number): Promise<Finding> =>
    // SW2-2321: converted from a `navigator.onLine` pre-check. After an offline
    // reload opening a finding hit the network and failed rather than reading the
    // cached record.
    withOfflineFallback<Finding>(
      () =>
        instance.get(`/api/findings/${id}`).then((response) => response.data),
      () => db.findings.get(Number(id)) as Promise<Finding | undefined>,
    ),

  create: async (
    inspectionId: number,
    formData: { [k: string]: any; images: IUpload[] },
    // SW2-2321: `opts.replay` must be passed when this call is the flush of an
    // already-queued write, so an unreachable server rejects instead of
    // re-queueing a write the sync loop is about to mark as synced.
    opts: { replay?: boolean } = {},
  ) => {
    const { images, ...data } = formData;

    // SW2-2321: was a `navigator.onLine` pre-check. After an offline reload the
    // POST was attempted, failed, and createFindingOffline never ran — the
    // finding and its photos were lost with no error and nothing queued.
    return withOfflineWrite<any>(
      async () => {
        const newFinding = await instance
          .post(url(inspectionId), data)
          .then((response) => response.data);

        // SW2-2321: skipped on replay. A replay is the flush of a row that is
        // ALREADY in IndexedDB, and these mirrors are create-style — they append
        // a second copy beside it (and `createOfflineCertification(..., true)`
        // also clears the entire remaining payload array, losing every signature
        // after the first). Replay must be wire-only: the sync loop reconciles
        // the local row itself. (Raised by Copilot on PR #53.)
        if (!opts.replay) {
          await createFindingOffline(
            inspectionId,
            formData,
            true,
            newFinding.id,
          );
        }

        return newFinding.dissolveFindingImages === false
          ? await uploadImages(newFinding, images, true)
          : { ...newFinding, finding_uploads: [] };
      },
      () =>
        // The previous offline branch called `.catch` on a plain object literal
        // — `{ ...finding, finding_uploads: [] }.catch(...)` — which is a
        // TypeError, not a no-op, so any finding created offline without
        // dissolved images threw here. Restructured so the catch wraps the
        // promise chain instead.
        createFindingOffline(inspectionId, formData)
          .then((finding: any) =>
            finding.dissolveFindingImages === false
              ? uploadImages(finding, images)
              : { ...finding, finding_uploads: [] },
          )
          .catch((error: any) => {
            console.log('Error creating offline finding', error);

            return Promise.reject(error);
          }),
      opts,
    );
  },

  destroy: (id: number, note: string) =>
    // SW2-2321: was a `navigator.onLine` pre-check. After an offline reload the
    // DELETE was attempted, failed, and the local delete never ran — so a finding
    // the inspector had deleted reappeared.
    withOfflineWrite<any>(
      () =>
        instance
          .delete(`/api/findings/${id}`, { data: { note: note } })
          .then((response) => response.data),
      () =>
        db.findings
          .delete(id)
          .then(() => {
            return db.findings
              .where('id')
              .equals(id)
              .toArray()
              .then((findings: any) => {
                return findings;
              });
          })
          .catch((error: any) => {
            console.log('Error deleting offline finding', error);
            return Promise.reject(error);
          }),
      // SW2-2321: `localOnlyWhenOffline` because nothing replays this local
      // write — the sync loop handles only create/update payloads. Resolving on
      // an unreachable server would claim the change will sync when it never
      // will, and the next download would restore the server's version.
      // (Raised by Copilot on PR #53.)
      { localOnlyWhenOffline: true },
    ),

  update: async (
    id: number,
    formData: { [k: string]: any; images: IUpload[] },
    // SW2-2321: `opts.replay` must be passed when this call is the flush of an
    // already-queued write, so an unreachable server rejects instead of
    // re-queueing a write the sync loop is about to mark as synced.
    opts: { replay?: boolean } = {},
  ) => {
    const { images, completedImages, ...data } = formData;

    // SW2-2321: was a `navigator.onLine` pre-check. After an offline reload the
    // POST was attempted, failed, and updateFindingOffline never ran — the edit
    // and any new photos were lost silently.
    // Set once the closure photos have reached the server, so that if the save
    // then fails on the network, the offline fallback does not queue them a
    // second time (a photo is only "existing" to extractUploads once it has an
    // id, and a successful upload sets only its guid).
    let closureUploadedOnline = false;

    return withOfflineWrite<any>(
      async () => {
        // SW2-2136: closure photos go up BEFORE the finding is saved. The save
        // is what closes the finding, and the server now refuses a close with
        // no closure photo when the client requires one — so a photo added in
        // the same save has to exist by then, as it already does on the bulk
        // path (CompleteFindingsModal uploads first). The finding exists, so
        // its id is all the upload needs. With photo dissolving on, the server
        // deletes these as the finding closes, the same end state as before.
        //
        // Not on a replay: the offline sync uploads the queued photos, with
        // their type, before it replays the save (InspectionsDue
        // syncPendingFindingPhotos), so uploading here as well would duplicate
        // them, and a File iOS has since reclaimed would stop the save syncing.
        const closureResult = opts.replay
          ? { finding_uploads: [] }
          : await uploadImages({ id }, completedImages, true, 'closure');

        closureUploadedOnline = !opts.replay;

        // Mark what just went up as existing (an id makes a photo "existing"
        // to extractUploads), so a retry after a refused save — a signed
        // inspection, an invalid type — does not upload the same photos again.
        markUploaded(completedImages, closureResult.finding_uploads);

        const updatedFinding = await instance
          .post(`/api/findings/${id}`, data)
          .then((response) => response.data);

        // SW2-2321: awaited (it was fire-and-forget, so the request callback
        // resolved before IndexedDB was written and a rejection became an
        // unhandled promise) and skipped on replay, where the row already
        // exists. (Raised by Copilot on PR #53.)
        if (!opts.replay) {
          await updateFindingOffline(id, formData, true);
        }
        if (updatedFinding.dissolveFindingImages === false) {
          const initialResult = await uploadImages(
            updatedFinding,
            images,
            true,
            'initial',
          );

          // Each online upload call returns the finding's COMPLETE upload list
          // (initial + closure) from the server. The initial call now runs
          // last, so its response is the authoritative snapshot whenever it hit
          // the server; fall back to the closure call's when there were no new
          // initial photos (an empty image list short-circuits to []).
          return {
            ...updatedFinding,
            finding_uploads:
              initialResult.finding_uploads.length > 0
                ? initialResult.finding_uploads
                : closureResult.finding_uploads,
          };
        }
        return { ...updatedFinding, finding_uploads: [] };
      },
      () =>
        updateFindingOffline(id, formData)
          .then((finding: any) => {
            if (finding.dissolveFindingImages === false) {
              return uploadImages(finding, images, false, 'initial').then(() =>
                uploadImages(
                  finding,
                  closureUploadedOnline ? [] : completedImages,
                  false,
                  'closure',
                ),
              );
            }
            return { ...finding, finding_uploads: [] };
          })
          .catch((error: any) => {
            console.log('Error updating offline finding', error);

            return Promise.reject(error);
          }),
      opts,
    );
  },

  bulk: {
    complete: async (
      inspectionId: number,
      data: {
        [imageKeyName: string]: IUpload[];
      } & { finding_ids: number[]; date_completed: Date; comments: string },
    ) => {
      const {
        finding_ids: findingIds,
        date_completed: dateCompleted,
        comments,
        completed_by,
      } = data;
      const findingUploads = new Map<number, IUpload[]>();

      findingIds.forEach((id) => {
        const imagesKey = `images_${id}`;

        if (Object.prototype.hasOwnProperty.call(data, imagesKey)) {
          findingUploads.set(id, data[imagesKey]);
        }
      });

      // SW2-2136: photos added in Complete Findings are closure photos, and they
      // go up BEFORE the findings are closed. They used to be uploaded after the
      // PUT, and as 'initial', so with closure photos required the server found
      // none and refused the close, and the photos printed as Initial.
      let photosUploadedOnline = false;

      // SW2-2321: was a `navigator.onLine` pre-check. Bulk-completing findings
      // after an offline reload sent the PUT, failed, and never ran
      // updateFindingOffline — losing the completion for every finding selected.
      return withOfflineWrite<any>(
        async () => {
          const uploadResults = new Map<number, any[]>();

          for (const findingId of findingIds) {
            const photos = findingUploads.get(findingId) || [];
            const result = await uploadImages(
              { id: findingId },
              photos,
              true,
              'closure',
            );

            uploadResults.set(findingId, result.finding_uploads || []);
            markUploaded(photos, result.finding_uploads);
          }

          photosUploadedOnline = true;

          const {
            data: findings,
          }: { data: { id: number; dissolveFindingImages: boolean }[] } =
            await xhrService.put(`/api/inspections/${inspectionId}/findings`, {
              finding_ids: findingIds,
              date_completed: dateCompleted,
              comments,
              completed_by,
            });

          for (const findingId of findingIds) {
            const formData = {
              date_completed: dateCompleted,
              comments: comments,
              images: findingUploads.get(findingId) || [],
              completedImages: [],
              inspectionId: inspectionId,
              bulk: true,
            };

            await updateFindingOffline(findingId, formData, true);
          }

          // Photos are already up. With dissolving on, the server deleted them as
          // it closed the findings, so there is nothing to show.
          if (findings[0] && findings[0].dissolveFindingImages === false) {
            return findings.map((finding) => ({
              ...finding,
              finding_uploads: uploadResults.get(finding.id) || [],
            }));
          }

          return findings;
        },
        async () => {
          for (const findingId of findingIds) {
            const formData = {
              date_completed: dateCompleted,
              comments: comments,
              images: findingUploads.get(findingId) || [],
              completedImages: [],
              inspectionId: inspectionId,
              bulk: true,
            };

            await updateFindingOffline(findingId, formData);
          }

          const findings = await db.findings
            .where('id')
            .anyOf(findingIds)
            .toArray();

          if (findings[0] && !findings[0].dissolveFindingImages) {
            return await Promise.all(
              findings.map((finding: any) =>
                uploadImages(
                  finding,
                  // Already on the server if the PUT failed after the uploads.
                  photosUploadedOnline ? [] : findingUploads.get(finding.id),
                  false,
                  'closure',
                ),
              ),
            );
          }

          return findings;
        },
      );
    },
    addNotes: async (
      inspectionId: number,
      findingIds: number[],
      comments: any,
    ) => {
      // SW2-2321: was a `navigator.onLine` pre-check. Notes added after an
      // offline reload were posted, failed, and never written locally.
      return withOfflineWrite<any>(
        async () => {
          const noteData = await xhrService.post(
            `/api/inspections/${inspectionId}/findings/notes`,
            {
              findingIds,
              comments,
            },
          );

          for (const findingId of findingIds) {
            const formData = {
              comments: comments,
              inspectionId: inspectionId,
              bulk: true,
              images: [],
              completedImages: [],
            };

            await updateFindingOffline(findingId, formData, true);
          }

          return noteData;
        },
        async () => {
          for (const findingId of findingIds) {
            const formData = {
              comments: comments,
              inspectionId: inspectionId,
              bulk: true,
              images: [],
              completedImages: [],
            };

            await updateFindingOffline(findingId, formData);
          }

          const inspection = await db.inspections.get(Number(inspectionId));
          const inspectionFindings = inspection ? inspection.findings : [];
          const findings = await db.findings
            .where('id')
            .anyOf(inspectionFindings.map((f: any) => f.id))
            .toArray();

          return { data: findings };
        },
      );
    },
  },
  complianceImpact: (
    findingId: number,
    type: string,
    inspectionId: number,
  ): Promise<{
    affected_inspections: Array<{
      id: number;
      created_date: string;
      type: string;
    }>;
  }> =>
    instance
      .get(`/api/findings/${findingId}/compliance-impact`, {
        params: { type, inspectionId },
      })
      .then((response) => response.data),

  addSignature: (actionId: number) => {
    return xhrService.get(`/api/findings/${actionId}/cert`);
  },
  removeSignature: (actionId: number) => {
    return xhrService.delete(`/api/findings/${actionId}/actionsign`);
  },
  deleteNotes: (findingId: number, actionId: number) => {
    return xhrService.delete(`/api/findings/${findingId}/cert/${actionId}`);
  },
};

export default findingApi;
